/**
 * Boot-time auth readiness gate.
 *
 * On a cold boot with an overnight-expired token, the first wave of
 * authenticated requests (onboarding status, journey data, training surfaces)
 * fires before MSAL has settled and before a usable token exists. Every
 * request 401s, and the page renders half-populated because React Query
 * (retry: 1, staleTime 5m) never refetches on its own.
 *
 * The gate holds authenticated requests until a usable (non-expired) bearer
 * token is in place, or until auth is known to be unrecoverable — session
 * marked expired, or timeout — at which point held requests are released to
 * fail fast into the normal 401 → SessionExpiredModal path.
 *
 * The gate only ever CLOSES once, at boot, and only when silent recovery is
 * plausible (a cached MSAL account exists but the stored token is unusable).
 * Mid-session expiry is handled by the apiClient/authenticatedFetch 401
 * queue-and-replay path, not by this gate.
 */

import { sessionManager } from './sessionManager'

// Must comfortably exceed AuthProvider's bounded MSAL settle-wait (8s) plus a
// silent token acquisition, but still release a genuinely wedged boot.
const GATE_TIMEOUT_MS = 12000
// Treat tokens expiring this soon as already dead — a request that leaves now
// could still arrive at the gateway after expiry.
const TOKEN_EXPIRY_MARGIN_MS = 30000
const POLL_INTERVAL_MS = 250

let state: 'unevaluated' | 'open' | 'blocked' = 'unevaluated'
let gatePromise: Promise<void> | null = null
let resolveGate: (() => void) | null = null
let pollTimer: ReturnType<typeof setInterval> | undefined
let timeoutTimer: ReturnType<typeof setTimeout> | undefined
let unsubscribeSession: (() => void) | undefined

const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/'

/** Dependency-free base64url → bytes (atob is unavailable/broken in some test DOMs). */
function base64UrlToBytes(input: string): Uint8Array | null {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/').replace(/=+$/, '')
  const bytes: number[] = []
  let buffer = 0
  let bits = 0
  for (const char of normalized) {
    const value = B64_ALPHABET.indexOf(char)
    if (value === -1) return null
    buffer = (buffer << 6) | value
    bits += 6
    if (bits >= 8) {
      bits -= 8
      bytes.push((buffer >> bits) & 0xff)
    }
  }
  return new Uint8Array(bytes)
}

/** Decode a JWT's exp claim (epoch seconds). Returns null when undecodable. */
export function decodeJwtExp(token: string): number | null {
  try {
    const payloadPart = token.split('.')[1]
    if (!payloadPart) return null
    const bytes = base64UrlToBytes(payloadPart)
    if (!bytes) return null
    const json =
      typeof TextDecoder !== 'undefined'
        ? new TextDecoder('utf-8').decode(bytes)
        : String.fromCharCode(...bytes)
    const payload = JSON.parse(json)
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

/** Stored token exists, decodes, and isn't expired (or about to expire). */
function storedTokenIsUsable(): boolean {
  try {
    const token = localStorage.getItem('access_token')
    if (!token) return false
    const exp = decodeJwtExp(token)
    if (exp === null) return false
    return exp * 1000 > Date.now() + TOKEN_EXPIRY_MARGIN_MS
  } catch {
    return false
  }
}

/**
 * Whether MSAL has a cached account — i.e. silent token recovery is
 * plausible. msal-browser (localStorage cache) tracks account entries in
 * `msal.account.keys`; the fallback scan catches cache-shape drift. Any MSAL
 * residue closes the gate at worst until refresh fails → expired → open.
 */
function hasCachedMsalAccount(): boolean {
  try {
    const keys = localStorage.getItem('msal.account.keys')
    if (keys) {
      const parsed = JSON.parse(keys)
      if (Array.isArray(parsed) && parsed.length > 0) return true
    }
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) || ''
      if (key.includes('login.windows.net') || key.includes('login.microsoftonline.com')) {
        return true
      }
    }
  } catch {
    // localStorage disabled — nothing cached, nothing to wait for
  }
  return false
}

function openGate(reason: string): void {
  if (state === 'open') return
  state = 'open'
  if (pollTimer !== undefined) clearInterval(pollTimer)
  if (timeoutTimer !== undefined) clearTimeout(timeoutTimer)
  pollTimer = undefined
  timeoutTimer = undefined
  unsubscribeSession?.()
  unsubscribeSession = undefined
  if (import.meta.env.DEV) console.log(`🔓 Auth readiness gate opened (${reason})`)
  resolveGate?.()
  resolveGate = null
}

function evaluateGate(): void {
  if (state !== 'unevaluated') return

  // Demo mode bypasses real auth entirely (persona headers, no bearer token).
  // DEV-only by construction: without the DEV guard, a stray .env.local with
  // VITE_DEMO_MODE=true in the Docker build context compiles this branch to
  // `state = 'open'` unconditionally and dead-code-eliminates the entire gate
  // (which is exactly what happened to the 2026-07-14 PROD build).
  if (import.meta.env.DEV && import.meta.env.VITE_DEMO_MODE === 'true') {
    state = 'open'
    return
  }

  // Usable token → nothing to wait for. No cached MSAL account → silent
  // recovery is impossible, so holding requests would only delay the
  // inevitable 401/login; also keeps public/anonymous routes unblocked.
  if (storedTokenIsUsable() || !hasCachedMsalAccount()) {
    state = 'open'
    return
  }

  state = 'blocked'
  console.warn(
    '⛔ Stored token missing/expired but an MSAL account is cached — holding API requests until a fresh token is acquired'
  )
  gatePromise = new Promise<void>((resolve) => {
    resolveGate = resolve
  })
  // Poll catches every code path that writes a fresh token, even ones that
  // forget to call signalAuthTokenReady().
  pollTimer = setInterval(() => {
    if (storedTokenIsUsable()) openGate('fresh token detected')
  }, POLL_INTERVAL_MS)
  timeoutTimer = setTimeout(() => {
    console.warn(`⚠️ Auth readiness gate timed out after ${GATE_TIMEOUT_MS}ms — releasing held requests`)
    openGate('timeout')
  }, GATE_TIMEOUT_MS)
  // Refresh declared the session dead → release requests to fail fast; the
  // SessionExpiredModal owns recovery from here.
  unsubscribeSession = sessionManager.subscribe((s) => {
    if (s === 'expired') openGate('session expired')
  })
}

/** True once authenticated requests may proceed. Evaluates lazily on first call. */
export function isAuthReady(): boolean {
  evaluateGate()
  return state === 'open'
}

/** Resolves immediately when the gate is open; otherwise when it opens. */
export function waitForAuthReady(): Promise<void> {
  evaluateGate()
  if (state === 'open' || !gatePromise) return Promise.resolve()
  return gatePromise
}

/** AuthProvider calls this right after storing a freshly-acquired token. */
export function signalAuthTokenReady(): void {
  if (state === 'blocked') openGate('token ready signal')
  else state = 'open'
}

/** Test-only: reset module state between cases. */
export function __resetAuthReadinessForTests(): void {
  if (pollTimer !== undefined) clearInterval(pollTimer)
  if (timeoutTimer !== undefined) clearTimeout(timeoutTimer)
  pollTimer = undefined
  timeoutTimer = undefined
  unsubscribeSession?.()
  unsubscribeSession = undefined
  resolveGate?.()
  resolveGate = null
  gatePromise = null
  state = 'unevaluated'
}
